import api from './api'
import type { Conversation, Message, PaginatedResponse } from '@/types'
import type { ApiResponse, SendMessageRequest } from '@/types/api'

const MessagesService = {
  async getConversations(
    instanceId: string,
    params?: {
      status?: string
      search?: string
      page?: number
      per_page?: number
    },
  ): Promise<PaginatedResponse<Conversation>> {
    const response = await api.get<PaginatedResponse<Conversation>>(
      `/api/v1/instances/${instanceId}/conversations`,
      { params },
    )
    return response.data
  },

  async getWorkspaceConversations(
    params?: {
      status?: string
      search?: string
      page?: number
      per_page?: number
      instance_id?: string
    },
  ): Promise<PaginatedResponse<Conversation>> {
    const response = await api.get<PaginatedResponse<Conversation>>(
      `/api/v1/conversations`,
      { params },
    )
    return response.data
  },

  async getMessages(
    instanceId: string,
    contactId: string,
    params?: { page?: number; per_page?: number },
  ): Promise<PaginatedResponse<Message>> {
    const query = {
      page: 1,
      per_page: 90,
      ...params,
    }
    const response = await api.get<PaginatedResponse<Message>>(
      `/api/v1/instances/${instanceId}/conversations/${contactId}/messages`,
      { params: query },
    )
    return response.data
  },

  async getMessagesByConversation(
    conversationId: string,
    params?: { page?: number; per_page?: number },
  ): Promise<PaginatedResponse<Message>> {
    const query = {
      page: 1,
      per_page: 90,
      ...params,
    }
    const response = await api.get<PaginatedResponse<Message>>(
      `/api/v1/conversations/${conversationId}/messages`,
      { params: query },
    )
    return response.data
  },

  async sendMessage(
    instanceId: string,
    data: SendMessageRequest,
  ): Promise<Message> {
    const response = await api.post<ApiResponse<Message>>(
      `/api/v1/instances/${instanceId}/messages/send-text`,
      {
        to: data.to_jid,
        text: data.content,
      },
    )
    return response.data.data
  },

  async markAsRead(_instanceId: string, messageId: string): Promise<void> {
    await api.put(`/api/v1/messages/${messageId}/status`, { status: 'read' })
  },

  async closeConversation(
    instanceId: string,
    conversationId: string,
  ): Promise<Conversation> {
    const response = await api.patch<ApiResponse<Conversation>>(
      `/api/v1/instances/${instanceId}/conversations/${conversationId}/finalize`,
    )
    return response.data.data
  },

  async assignConversation(
    instanceId: string,
    conversationId: string,
    userId: string,
  ): Promise<void> {
    await api.patch(
      `/api/v1/instances/${instanceId}/conversations/${conversationId}/assign`,
      { user_id: userId },
    )
  },
}

export default MessagesService
